import React, { useMemo, memo } from 'react'
import { View } from 'react-native'

import { getPractitionerFromChat } from 'APP/Lib/PractitionerHelpers'

import moment from 'moment'
import { useTheme } from 'styled-components/native'
import I18n from 'APP/I18n'

import Avatar from 'APP/Components/Chat/Avatar'
import PostReceipt from 'APP/Components/Chat/PostReceipt'
import SingleChoice from 'APP/Components/Chat/SingleChoice'
import { ResponsiveView } from 'APP/Converse/Layout'
import { useChatContext, usePostContext } from 'APP/Lib/Context'
import {
  getPostSubtext,
  isCalendar,
  isCmsContent,
  onlyDisplayedWhenLastPost,
} from 'APP/Lib/MessagingHelpers'
import DialogueMarkdown from 'APP/Components/DialogueMarkdown'
import MultiChoice from 'APP/Components/Chat/MultiChoice'
import ProviderList from 'APP/Components/Chat/ProviderList'
import { getPostComponent } from './getPostComponent'
import {
  LeftContainer,
  RightContainer,
  ColumnContainer,
  PostContainerLeft,
  PostContainerRight,
  MarkdownContainer,
  DateContainer,
  DateText,
  ErrorText,
  NameContainer,
  LeftName,
} from './styles'

const Post: React.FC = () => {
  const theme = useTheme()
  const { users } = useChatContext()
  const {
    post,
    previousPostIsSameUser,
    isMember,
    nextPostIsSameUser,
    previousPostIsSameDay,
    nextPostIsSameDay,
    isLastPost,
  } = usePostContext()
  const PostComponent = useMemo(() => getPostComponent(post), [post])

  const markdownStyles = {
    body: {
      color: theme.colors.buttonPrimarySelected,
      fontSize: 12,
    },
    ul: {
      marginTop: 0,
      marginBottom: theme.metrics.baseMargin / 2,
      marginLeft: -12,
    },
  }

  const slimContainerStyle = {
    marginBottom: theme.metrics.baseMargin / 4,
  }

  const renderLastPostComponent = () => {
    if (!isLastPost) return null
    // calendar and cms content is a single choice message type
    // but we don't want to render it if it's the last post
    if (isCalendar(post) || isCmsContent(post)) return null

    const postSubtext = getPostSubtext(post)

    const getOptionsContainer = () => {
      if (post?.props?.type === 'prompt_choice_multi' || post?.props?.type === 'multiSelect.v1') {
        return (
          <PostContainerRight>
            <MultiChoice />
          </PostContainerRight>
        )
      }
      if (post?.props?.type === 'prompt_provider_list') {
        return (
          <PostContainerRight>
            <ProviderList />
          </PostContainerRight>
        )
      }
      if ((post?.props?.choices?.length || 0) > 0) {
        return (
          <PostContainerRight>
            <SingleChoice />
          </PostContainerRight>
        )
      }
      return null
    }
    const optionsContainer = getOptionsContainer()

    return (
      <>
        {postSubtext && (
          <MarkdownContainer>
            <DialogueMarkdown styles={markdownStyles} markdown={postSubtext} containerStyles={{}} />
          </MarkdownContainer>
        )}
        {optionsContainer}
      </>
    )
  }

  const renderDateComponent = () => {
    if (previousPostIsSameDay) return null

    return (
      <DateContainer>
        <DateText variant="caption">{moment(post?.create_at).calendar()}</DateText>
      </DateContainer>
    )
  }

  if (!isLastPost && onlyDisplayedWhenLastPost(post)) {
    return null
  }

  const slimContainer = nextPostIsSameUser && nextPostIsSameDay ? slimContainerStyle : {}

  if (isMember) {
    return (
      <ResponsiveView>
        {renderDateComponent()}
        <RightContainer style={{ ...slimContainer }}>
          <PostContainerRight>
            <PostComponent />
          </PostContainerRight>
          {renderLastPostComponent()}
          {post?.props?.failed ? (
            <View>
              {post?.props.errorMessage && (
                <ErrorText variant="caption" color="errorText">
                  {post?.props.errorMessage}
                </ErrorText>
              )}
              <ErrorText variant="caption" color="errorText">
                {I18n.t('ConversationScreen.errorSending')}
              </ErrorText>
            </View>
          ) : (
            <PostReceipt />
          )}
        </RightContainer>
      </ResponsiveView>
    )
  }

  const { first_name, last_name } = getPractitionerFromChat({ post, isUser: false, users }) || {}
  const name = [first_name, last_name].filter((el) => el).join(' ') || I18n.t('Common.clinic')

  return (
    <ResponsiveView>
      {renderDateComponent()}
      <LeftContainer style={{ ...slimContainer }}>
        <PostContainerLeft>
          <Avatar />
          <ColumnContainer>
            {(!previousPostIsSameUser || !previousPostIsSameDay) && (
              <NameContainer>
                <LeftName variant="caption" color="textSecondary">
                  {name}
                </LeftName>
              </NameContainer>
            )}
            <PostComponent />
            {renderLastPostComponent()}
          </ColumnContainer>
        </PostContainerLeft>
      </LeftContainer>
    </ResponsiveView>
  )
}

export default memo(Post)
